import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  X,
  Loader2,
  Plus,
  Save,
  ChevronDown,
  ChevronUp,
  Crown,
  Layers,
  ShieldAlert,
  AlertTriangle,
  Lock,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/client/lib/api';
import { useKeyboard } from '@/client/hooks/use-keyboard';
import { useFeatureGate, TierBadge } from '@/client/components/feature-gate';

interface ProjectOption {
  id: string;
  name: string;
  color: string;
}

// Schema for updating a link (matches backend updateLinkSchema)
const updateLinkSchema = z.object({
  title: z.string().max(255).optional(),
  destinationUrl: z.string().url().max(2048).optional(),
  tags: z.array(z.string()).optional(),
  projectId: z.string().nullable().optional(),
  validUntil: z.string().nullable().optional(),
  maxVisits: z.number().int().positive().max(1_000_000).nullable().optional(),
  isActive: z.boolean().optional(),
  password: z.string().min(4).max(128).nullable().optional(),
  passwordHint: z.string().max(100).nullable().optional(),
});

type UpdateLinkInput = z.infer<typeof updateLinkSchema>;

interface EditLinkModalProps {
  link: {
    id: string;
    shortCode: string;
    shortUrl: string;
    destinationUrl: string;
    title?: string | null;
    tags?: string[];
    projectId?: string | null;
    validUntil?: string | null;
    maxVisits?: number | null;
    isActive?: boolean;
    hasPassword?: boolean;
    passwordHint?: string | null;
  } | null;
  isOpen: boolean;
  onClose: () => void;
}

export function EditLinkModal({ link, isOpen, onClose }: EditLinkModalProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showUrlWarning, setShowUrlWarning] = useState(false);
  const [unsafeUrlError, setUnsafeUrlError] = useState<string | null>(null);
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const queryClient = useQueryClient();

  // Feature gates for Plus features
  const {
    hasAccess: hasProjectAccess,
    requiredTier: projectRequiredTier,
    openUpgradeModal: openProjectUpgradeModal,
    UpgradeModalElement: ProjectUpgradeModal,
  } = useFeatureGate('project_limits', 'Organize links into projects for better management');

  const {
    hasAccess: hasPasswordAccess,
    requiredTier: passwordRequiredTier,
    openUpgradeModal: openPasswordUpgradeModal,
    UpgradeModalElement: PasswordUpgradeModal,
  } = useFeatureGate('link_password_protection', 'Protect your links with password authentication');

  // Fetch projects for Plus users
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiFetch('/api/projects');
      if (!response.ok) return { projects: [] };
      return response.json();
    },
    enabled: hasProjectAccess && isOpen,
  });

  const projects: ProjectOption[] = projectsData?.projects || projectsData?.data || [];

  const form = useForm<UpdateLinkInput>({
    resolver: zodResolver(updateLinkSchema),
    defaultValues: {
      title: '',
      destinationUrl: '',
      tags: [],
      projectId: null,
      validUntil: null,
      maxVisits: null,
      isActive: true,
    },
  });

  // Reset form when link changes
  useEffect(() => {
    if (link && isOpen) {
      form.reset({
        title: link.title || '',
        destinationUrl: link.destinationUrl,
        tags: link.tags || [],
        projectId: link.projectId || null,
        validUntil: link.validUntil ? link.validUntil.slice(0, 16) : null,
        maxVisits: link.maxVisits || null,
        isActive: link.isActive !== false,
        password: null,
        passwordHint: link.passwordHint || null,
      });
      setShowAdvanced(false);
      setTagInput('');
      setShowUrlWarning(false);
      setUnsafeUrlError(null);
      setPasswordEnabled(!!link.hasPassword);
      setShowPassword(false);
    }
  }, [link, isOpen, form]);

  const updateLinkMutation = useMutation({
    mutationFn: async (data: UpdateLinkInput) => {
      setUnsafeUrlError(null);

      // Only include changed fields
      const payload: Partial<UpdateLinkInput> = {};

      if (data.title !== (link?.title || '')) {
        payload.title = data.title || undefined;
      }
      if (data.destinationUrl !== link?.destinationUrl) {
        payload.destinationUrl = data.destinationUrl;
      }
      if (JSON.stringify(data.tags) !== JSON.stringify(link?.tags || [])) {
        payload.tags = data.tags;
      }
      // Convert empty string to null for project removal
      const newProjectId = data.projectId === '' ? null : data.projectId;
      if (newProjectId !== (link?.projectId || null)) {
        payload.projectId = newProjectId;
      }
      if (data.isActive !== (link?.isActive !== false)) {
        payload.isActive = data.isActive;
      }

      // Handle validUntil - convert to ISO string if set
      const currentValidUntil = link?.validUntil ? link.validUntil.slice(0, 16) : null;
      if (data.validUntil !== currentValidUntil) {
        payload.validUntil = data.validUntil ? new Date(data.validUntil).toISOString() : null;
      }

      // Handle maxVisits
      if (data.maxVisits !== (link?.maxVisits || null)) {
        payload.maxVisits = data.maxVisits;
      }

      // Handle password protection
      if (!passwordEnabled && link?.hasPassword) {
        // User wants to remove password protection
        payload.password = '';
        payload.passwordHint = null;
      } else if (passwordEnabled) {
        // User wants to add/update password
        if (data.password) {
          payload.password = data.password;
        }
        if (data.passwordHint !== (link?.passwordHint || null)) {
          payload.passwordHint = data.passwordHint;
        }
      }

      // Don't make request if nothing changed
      if (Object.keys(payload).length === 0) {
        return { noChanges: true };
      }

      const response = await apiFetch(`/api/links/${link?.shortCode}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.code === 'UNSAFE_URL') {
          const unsafeError = new Error(error.message) as Error & {
            isUnsafe: boolean;
            threatType: string;
          };
          unsafeError.isUnsafe = true;
          unsafeError.threatType = error.threatType;
          throw unsafeError;
        }
        throw new Error(error.message || error.error || 'Failed to update link');
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      if (data?.noChanges) {
        toast.info('No changes to save');
        onClose();
        return;
      }
      toast.success('Link updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['links'] });
      queryClient.invalidateQueries({ queryKey: ['links', link?.shortCode] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });

      // Invalidate project detail caches if projectId changed
      if (variables.projectId !== undefined) {
        // Invalidate old project (the one the link was in before)
        if (link?.projectId) {
          queryClient.invalidateQueries({ queryKey: ['project', link.projectId] });
          queryClient.invalidateQueries({ queryKey: ['project-analytics', link.projectId] });
        }
        // Invalidate new project (where the link is moving to)
        // Note: variables.projectId could be null (moving to default), but the API converts it to the actual default project ID
        // We invalidate all project queries to be safe
        queryClient.invalidateQueries({ queryKey: ['project'] });
        queryClient.invalidateQueries({ queryKey: ['project-analytics'] });
      }
      onClose();
    },
    onError: (error: Error & { isUnsafe?: boolean }) => {
      if (error.isUnsafe) {
        setUnsafeUrlError(error.message);
      } else {
        toast.error(error.message || 'Failed to update link');
      }
    },
  });

  const onSubmit = (data: UpdateLinkInput) => {
    // Show warning if URL is being changed
    if (data.destinationUrl !== link?.destinationUrl && !showUrlWarning) {
      setShowUrlWarning(true);
      return;
    }
    updateLinkMutation.mutate(data);
  };

  const confirmUrlChange = () => {
    const data = form.getValues();
    updateLinkMutation.mutate(data);
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !form.getValues('tags')?.includes(tag)) {
      form.setValue('tags', [...(form.getValues('tags') || []), tag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    form.setValue('tags', form.getValues('tags')?.filter((t) => t !== tagToRemove) || []);
  };

  // ⚡ Performance Optimization: Use shared keyboard hook instead of manual listener
  useKeyboard('Escape', onClose, isOpen);

  if (!isOpen || !link) return null;

  const tags = form.watch('tags') || [];
  const currentUrl = form.watch('destinationUrl');
  const urlChanged = currentUrl !== link.destinationUrl;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg mx-4 bg-background rounded-lg shadow-xl border animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-background z-10">
          <div>
            <h2 className="text-xl font-semibold">Edit Link</h2>
            <p className="text-sm text-muted-foreground mt-0.5 font-mono">
              arml.ink/{link.shortCode}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close edit link modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* URL Change Warning */}
        {showUrlWarning && (
          <div className="mx-6 mt-6 p-4 rounded-lg border border-amber-500/30 bg-amber-500/10">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-600">Confirm URL Change</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Changing the destination URL will affect all existing QR codes and shared links
                  pointing to <span className="font-mono text-xs">arml.ink/{link.shortCode}</span>.
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={confirmUrlChange}
                    disabled={updateLinkMutation.isPending}
                    className="px-3 py-1.5 text-sm font-medium rounded-md bg-amber-500 text-white hover:bg-amber-600 transition-colors disabled:opacity-50"
                  >
                    {updateLinkMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Yes, Update URL'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUrlWarning(false);
                      form.setValue('destinationUrl', link.destinationUrl);
                    }}
                    className="px-3 py-1.5 text-sm font-medium rounded-md border hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={form.handleSubmit(onSubmit)} className="p-6 space-y-4">
          {/* Destination URL */}
          <div>
            <label htmlFor="destinationUrl" className="block text-sm font-medium mb-2">
              Destination URL
            </label>
            <input
              type="url"
              id="destinationUrl"
              autoComplete="url"
              inputMode="url"
              spellCheck={false}
              {...form.register('destinationUrl')}
              className="w-full px-4 py-2.5 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="https://example.com/your-url"
            />
            {form.formState.errors.destinationUrl && (
              <p className="text-sm text-destructive mt-1">
                {form.formState.errors.destinationUrl.message}
              </p>
            )}
            {urlChanged && !showUrlWarning && (
              <p className="text-xs text-amber-500 mt-1 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                URL will be changed when you save
              </p>
            )}
            {unsafeUrlError && (
              <div className="mt-2 p-3 rounded-lg border border-orange-500/30 bg-orange-500/10">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-orange-600">Unable to update link</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      This URL has been flagged as potentially unsafe.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium mb-2">
              Title <span className="text-muted-foreground">(optional)</span>
            </label>
            <input
              type="text"
              id="title"
              autoComplete="off"
              spellCheck={true}
              {...form.register('title')}
              className="w-full px-4 py-2.5 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="My awesome link"
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <label htmlFor="isActive" className="text-sm font-medium">
                Link Status
              </label>
              <p className="text-xs text-muted-foreground">Inactive links will show a 404 error</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                id="isActive"
                {...form.register('isActive')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
            </label>
          </div>

          {/* Advanced Options Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            Advanced options
          </button>

          {/* Advanced Options */}
          {showAdvanced && (
            <div className="space-y-4 pt-2 border-t">
              {/* Project Selector - Plus Feature */}
              <div>
                <label
                  htmlFor="projectId"
                  className="flex items-center gap-2 text-sm font-medium mb-2"
                >
                  <Layers className="h-4 w-4" aria-hidden="true" />
                  Project
                  {!hasProjectAccess && <TierBadge tier={projectRequiredTier} size="xs" />}
                </label>
                {hasProjectAccess ? (
                  <select
                    id="projectId"
                    autoComplete="off"
                    {...form.register('projectId')}
                    className="w-full px-4 py-2 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">No project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <button
                    type="button"
                    onClick={openProjectUpgradeModal}
                    className="w-full px-4 py-3 rounded-md border border-dashed bg-muted/30 text-center hover:bg-muted/50 hover:border-primary/30 transition-colors cursor-pointer"
                  >
                    <p className="text-sm text-muted-foreground mb-1">
                      Organize links into projects
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                      <Crown className="h-3.5 w-3.5" aria-hidden="true" />
                      Upgrade to unlock
                    </span>
                  </button>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium mb-2">Tags</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="newTag"
                    autoComplete="off"
                    spellCheck={false}
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    className="flex-1 px-4 py-2 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Add a tag"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-3 py-2 rounded-md border hover:bg-muted transition-colors"
                    aria-label="Add tag"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="hover:text-primary/70"
                          aria-label={`Remove ${tag} tag`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Valid Until */}
              <div>
                <label htmlFor="validUntil" className="block text-sm font-medium mb-2">
                  Expiration Date
                </label>
                <input
                  type="datetime-local"
                  id="validUntil"
                  autoComplete="off"
                  {...form.register('validUntil')}
                  className="w-full px-4 py-2 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground mt-1">Leave empty for no expiration</p>
              </div>

              {/* Max Visits */}
              <div>
                <label htmlFor="maxVisits" className="block text-sm font-medium mb-2">
                  Maximum Visits
                </label>
                <input
                  type="number"
                  id="maxVisits"
                  autoComplete="off"
                  inputMode="numeric"
                  {...form.register('maxVisits', { valueAsNumber: true })}
                  className="w-full px-4 py-2 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Unlimited"
                  min={1}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave empty for unlimited visits
                </p>
              </div>

              {/* Password Protection */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <Lock className="h-4 w-4" aria-hidden="true" />
                    Password Protection
                    {!hasPasswordAccess && <TierBadge tier={passwordRequiredTier} size="xs" />}
                  </label>
                  {hasPasswordAccess ? (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="passwordEnabled"
                        checked={passwordEnabled}
                        onChange={(e) => {
                          setPasswordEnabled(e.target.checked);
                          if (!e.target.checked) {
                            form.setValue('password', null);
                            form.setValue('passwordHint', null);
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                    </label>
                  ) : null}
                </div>

                {hasPasswordAccess ? (
                  passwordEnabled ? (
                    <div className="space-y-3">
                      {/* Password Input */}
                      <div>
                        <label
                          htmlFor="password"
                          className="block text-sm text-muted-foreground mb-1"
                        >
                          {link?.hasPassword
                            ? 'New Password (leave empty to keep current)'
                            : 'Password'}
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="new-password"
                            spellCheck={false}
                            {...form.register('password')}
                            className="w-full px-4 py-2 pr-10 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder={link?.hasPassword ? '••••••••' : 'Enter password'}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        {!link?.hasPassword && (
                          <p className="text-xs text-muted-foreground mt-1">Minimum 4 characters</p>
                        )}
                      </div>

                      {/* Password Hint */}
                      <div>
                        <label
                          htmlFor="passwordHint"
                          className="block text-sm text-muted-foreground mb-1"
                        >
                          Password Hint <span className="text-muted-foreground/60">(optional)</span>
                        </label>
                        <input
                          type="text"
                          id="passwordHint"
                          autoComplete="off"
                          spellCheck={false}
                          {...form.register('passwordHint')}
                          className="w-full px-4 py-2 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="e.g., The secret code from the meeting"
                          maxLength={100}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Shown on the password page to help visitors remember
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Require visitors to enter a password before accessing this link.
                    </p>
                  )
                ) : (
                  <button
                    type="button"
                    onClick={openPasswordUpgradeModal}
                    className="w-full px-4 py-3 rounded-md border border-dashed bg-muted/30 text-center hover:bg-muted/50 hover:border-primary/30 transition-colors cursor-pointer"
                  >
                    <p className="text-sm text-muted-foreground mb-1">
                      Protect your links with a password
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                      <Crown className="h-3.5 w-3.5" aria-hidden="true" />
                      Upgrade to unlock
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateLinkMutation.isPending || showUrlWarning}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {updateLinkMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" aria-hidden="true" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Feature Upgrade Modals */}
      {ProjectUpgradeModal}
      {PasswordUpgradeModal}
    </div>
  );
}
